import type {
  Condition,
  ConditionalOperator,
  CustomPropertyConditionValue,
  CustomPropertySubOperator
} from 'Automations/nodeDefinitions/conditions'
import {
  customPropertyKeyFromOperatorId,
  isCustomPropertyOperatorId
} from 'Automations/nodeDefinitions/conditions/helpers/functionStrings'

// Static identity operators, listed first; the custom-property operators
// (one per key) follow in the order the property factory generated them.
export const CUSTOMER_IDENTITY_OPERATORS = ['INCLUDES', 'EXCLUDES'] as const

export const isCustomerIdentityOperator = (
  operator: string
): operator is 'INCLUDES' | 'EXCLUDES' =>
  CUSTOMER_IDENTITY_OPERATORS.includes(operator as 'INCLUDES' | 'EXCLUDES')

// Fields a CUSTOMER condition's value can carry, used to route validation
// errors to the right sub-input via `metadata.field`.
export const readCustomerIds = (value: unknown): string[] =>
  Array.isArray(value) ? (value as string[]) : []

export const appendTestId = (
  base: string | undefined,
  suffix: string
): string | undefined => (base ? `${base}.${suffix}` : undefined)

// The empty value shape seeded when switching to each operator. Identity
// operators store a `string[]` of customer ids; custom-property operators store
// `{ operator, value }` (the key lives in the operator id).
const emptyValueForOperator = (
  operator: string
): string[] | CustomPropertyConditionValue =>
  isCustomPropertyOperatorId(operator)
    ? { operator: 'EQUALS' as CustomPropertySubOperator }
    : []

/**
 * Builds the operator dropdown options — identity operators first, then one per
 * custom-property key (in the order the factory generated them) — and an
 * onChange that resets the stored value to the new operator's empty shape, since
 * identity operators (`string[]`) and custom-property operators
 * (`{ operator, value }`) carry incompatible value shapes.
 *
 * A custom-property key already chosen by a sibling row in the same group is
 * omitted, so each key can only be compared once per group. The current row's
 * own selection is always kept visible.
 */
export const useCustomerOperatorState = (params: {
  operator: string
  operators: Record<string, ConditionalOperator>
  siblingConditions: Array<Condition>
  onChange: (next: { operator?: string; value?: unknown }) => void
}): {
  operatorOptions: Array<{ label: string; value: string }>
  onOperatorChange: (selected: string | number) => void
} => {
  const { operator, operators, siblingConditions, onChange } = params

  // Custom-property operator ids already taken by sibling rows. Identity
  // operators (INCLUDES / EXCLUDES) are intentionally excluded — they can
  // legitimately appear on more than one row.
  const siblingCustomPropertyOperators = new Set(
    siblingConditions
      .map(condition => condition.operator)
      .filter(isCustomPropertyOperatorId)
  )

  const baseOptions = Object.keys(operators)
    .filter(key => key === operator || !siblingCustomPropertyOperators.has(key))
    .sort((a, b) => operatorSortIndex(a) - operatorSortIndex(b))
    .map(key => ({ label: operators[key]?.label ?? key, value: key }))

  // Keep the currently-selected operator visible even if its custom-property
  // key is no longer among the fetched keys (deleted, or a transient fetch
  // failure) — otherwise the dropdown would render blank and the user couldn't
  // see or change the stale selection. The key is recovered from the operator
  // id for the label.
  const currentMissing =
    Boolean(operator) && !baseOptions.some(option => option.value === operator)
  const operatorOptions = currentMissing
    ? [
        ...baseOptions,
        {
          label: customPropertyKeyFromOperatorId(operator) ?? operator,
          value: operator
        }
      ]
    : baseOptions

  const onOperatorChange = (selected: string | number) => {
    const nextOperator = String(selected)
    onChange({
      operator: nextOperator,
      value: emptyValueForOperator(nextOperator)
    })
  }

  return { operatorOptions, onOperatorChange }
}

// Keeps identity operators ahead of the custom-property operators; the latter
// retain their relative (factory) order.
const operatorSortIndex = (operator: string): number =>
  isCustomerIdentityOperator(operator) ? 0 : 1
